import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { lineHeight, theme, toRem } from 'design';
import { Icon } from '../Icon';

interface BreadcrumbItemProps {
  label: string;
  to?: string;
  onClick?: () => void;
  className?: string;
}

const BreadcrumbItemStyles = css`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: auto;
  margin-right: ${toRem(theme.space.sm)};
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(theme.font.size.xxs)};
  line-height: ${lineHeight(theme.font.size.xxs)};
  text-decoration: none;
  text-transform: capitalize;
  color: ${theme.color.secondary[300]};
  transition: color ${theme.transition.button.fast};

  @media (min-width: ${theme.breakpoint.small}px) {
    font-size: ${toRem(theme.font.size.sm)};
    line-height: ${lineHeight(theme.font.size.sm)};
  }

  svg {
    margin-left: ${toRem(theme.space.sm)};
    width: ${toRem(6)};
    fill: ${theme.color.neutral[400]};

    @media (min-width: ${theme.breakpoint.small}px) {
      width: ${toRem(8)};
    }
  }

  &:visited,
  &:active {
    color: ${theme.color.secondary[300]};
  }

  &:hover {
    color: ${theme.color.secondary[400]};
  }

  &:last-child {
    margin-right: 0;
    color: ${theme.color.neutral[400]};

    svg {
      display: none;
    }

    &:visited,
    &:active {
      color: ${theme.color.neutral[400]};
    }

    &:hover {
      color: ${theme.color.neutral[500]};
    }
  }
`;

const StyledLink = styled(Link)`
  ${BreadcrumbItemStyles}
  cursor: pointer;
`;

const InactiveLink = styled.span`
  ${BreadcrumbItemStyles}

  &:hover {
    color: ${theme.color.neutral[400]} !important;
    cursor: default;
  }
`;

const BreadcrumbsItem: React.FC<BreadcrumbItemProps> = ({ label, to, onClick, className, ...rest }) => {
  const innerContent = (
    <>
      <span>{label}</span>
      <Icon icon="Next" />
    </>
  );

  return to ? (
    <StyledLink to={to} onClick={onClick} className={className} {...rest}>
      {innerContent}
    </StyledLink>
  ) : (
    <InactiveLink className={className}>{innerContent}</InactiveLink>
  );
};

export default BreadcrumbsItem;
